import { useEffect, useState } from 'react';

import { EVENTO_AVISO, type AvisoRecibido } from '../lib/avisos';

/** Cuánto se queda visible antes de irse solo. */
const DURACION_MS = 6000;

/**
 * Muestra el aviso de visita cuando llega con la app en pantalla.
 *
 * Existe porque Android **no** dibuja la notificación si la app está abierta:
 * se la entrega a la aplicación dando por hecho que ya la está mostrando. Sin
 * esto, el residente que tiene Kodica en la mano es el único que no se entera
 * de que su visita llegó — justo el caso de una demostración.
 *
 * En el navegador nunca se dispara el evento, así que este componente no
 * estorba en la web de la caseta.
 */
export function AvisoEnPantalla() {
  const [aviso, setAviso] = useState<AvisoRecibido | null>(null);

  useEffect(() => {
    function alLlegar(e: Event) {
      setAviso((e as CustomEvent<AvisoRecibido>).detail);
    }
    window.addEventListener(EVENTO_AVISO, alLlegar);
    return () => window.removeEventListener(EVENTO_AVISO, alLlegar);
  }, []);

  useEffect(() => {
    if (!aviso) return;
    // El temporizador se reinicia con cada aviso nuevo porque depende de
    // `aviso`: dos visitas seguidas no se comen el tiempo la una a la otra.
    const reloj = setTimeout(() => setAviso(null), DURACION_MS);
    return () => clearTimeout(reloj);
  }, [aviso]);

  if (!aviso) return null;

  return (
    <button
      type="button"
      onClick={() => setAviso(null)}
      // `pt-[env(safe-area-inset-top)]` deja sitio a la muesca del teléfono;
      // sin eso el título queda debajo de la barra de estado en pantallas con
      // notch y se lee a medias.
      className="fixed inset-x-0 top-0 z-50 w-full pt-[env(safe-area-inset-top)] text-left"
    >
      <div className="m-3 rounded-2xl bg-primary px-4 py-3 text-on-primary shadow-lg">
        <p className="text-sm font-semibold">{aviso.titulo}</p>
        {aviso.cuerpo && <p className="mt-0.5 text-sm opacity-90">{aviso.cuerpo}</p>}
      </div>
    </button>
  );
}
